import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import DateInput from '../components/DateInput';
import { formatCurrency } from '../utils/currency';
import { formatDate, toIsoDate, todayIso } from '../utils/dates';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import './Products.css';
import './CustomerReport.css';

const firstOfMonth = () => {
  const d = new Date();
  return toIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
};

const SupplierReport = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({
    from: firstOfMonth(),
    to: todayIso(),
    search: '',
    balance: 'all'
  });
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async (nextFilters = filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (nextFilters.from) params.set('from', nextFilters.from);
      if (nextFilters.to) params.set('to', nextFilters.to);
      if (nextFilters.search) params.set('search', nextFilters.search);
      if (nextFilters.balance && nextFilters.balance !== 'all') {
        params.set('balance', nextFilters.balance);
      }

      const res = await axios.get(`/api/reports/suppliers?${params.toString()}`);
      setSummary(res.data.summary || null);
      setSuppliers(res.data.suppliers || []);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Failed to load supplier report');
    } finally {
      setLoading(false);
    }
  };

  const openStatement = async (supplier) => {
    setSelectedSupplier(supplier);
    setStatement(null);
    setStatementLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      const res = await axios.get(`/api/reports/suppliers/${supplier.id}?${params.toString()}`);
      setStatement(res.data);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load supplier statement');
      setSelectedSupplier(null);
    } finally {
      setStatementLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchReport(filters);
  };

  const clearFilters = () => {
    const reset = {
      from: firstOfMonth(),
      to: todayIso(),
      search: '',
      balance: 'all'
    };
    setFilters(reset);
    fetchReport(reset);
  };

  const balanceClass = (status) => {
    if (status === 'due') return 'balance-due';
    if (status === 'credit') return 'balance-credit';
    return 'balance-settled';
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading && !summary) {
    return (
      <div className="page-container">
        <div className="container">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-container customer-report-page">
      <div className="container">
        <div className="page-header no-print">
          <h1>Supplier Report</h1>
          <button type="button" className="btn btn-secondary" onClick={handlePrint}>
            Print
          </button>
        </div>

        <div className="card no-print" style={{ marginBottom: '16px' }}>
          <form onSubmit={applyFilters} className="report-filters">
            <div className="form-group">
              <label>From</label>
              <DateInput
                value={filters.from}
                onChange={(e) => handleFilterChange('from', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>To</label>
              <DateInput
                value={filters.to}
                onChange={(e) => handleFilterChange('to', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Name, phone, email"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Balance</label>
              <select
                value={filters.balance}
                onChange={(e) => handleFilterChange('balance', e.target.value)}
              >
                <option value="all">All</option>
                <option value="due">Due only</option>
                <option value="settled">Settled</option>
                <option value="credit">Credit balance</option>
              </select>
            </div>
            <div className="report-filter-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Loading...' : 'Apply'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={clearFilters}>
                Reset
              </button>
            </div>
          </form>
          <p className="report-help">
            Debit = purchases (amount payable to supplier). Credit = paid purchases.
            Balance = Debit − Credit (outstanding payable). Payment status on each purchase: Pending, Billed, or Paid.
          </p>
        </div>

        {summary && (
          <div className="report-summary-grid">
            <div className="report-summary-card">
              <span>Suppliers</span>
              <strong>{summary.supplier_count}</strong>
            </div>
            <div className="report-summary-card debit">
              <span>Total Debit (Purchases)</span>
              <strong>{formatCurrency(summary.total_debit)}</strong>
            </div>
            <div className="report-summary-card credit">
              <span>Total Credit (Paid)</span>
              <strong>{formatCurrency(summary.total_credit)}</strong>
            </div>
            <div className="report-summary-card balance">
              <span>Outstanding Payable</span>
              <strong>{formatCurrency(summary.total_balance)}</strong>
            </div>
            <div className="report-summary-card returns">
              <span>Purchases</span>
              <strong>{summary.purchase_count}</strong>
            </div>
          </div>
        )}

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Phone</th>
                <th>Purchases</th>
                <th>Debit</th>
                <th>Credit (Paid)</th>
                <th>Billed</th>
                <th>Pending</th>
                <th>Balance</th>
                <th className="no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>
                    No suppliers found for the selected filters
                  </td>
                </tr>
              ) : (
                suppliers.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="customer-name-cell">{row.name}</div>
                      {row.email ? <div className="customer-sub">{row.email}</div> : null}
                    </td>
                    <td>{row.phone || '-'}</td>
                    <td>{row.purchase_count}</td>
                    <td>{formatCurrency(row.total_debit)}</td>
                    <td>{formatCurrency(row.total_credit)}</td>
                    <td>{formatCurrency(row.billed_amount)}</td>
                    <td>{formatCurrency(row.pending_amount)}</td>
                    <td>
                      <span className={`balance-pill ${balanceClass(row.balance_status)}`}>
                        {formatCurrency(row.balance)}
                      </span>
                    </td>
                    <td className="no-print">
                      <KebabMenu
                        menuId={`supplier-report-${row.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        items={[
                          { label: 'Statement', icon: MENU_ICONS.view, onClick: () => openStatement(row) }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedSupplier && (
          <div className="modal statement-modal">
            <div className="modal-content statement-modal-content">
              <div className="modal-header no-print">
                <h2>Supplier Statement — {selectedSupplier.name}</h2>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => {
                    setSelectedSupplier(null);
                    setStatement(null);
                  }}
                >
                  ×
                </button>
              </div>

              {statementLoading || !statement ? (
                <div style={{ padding: '24px' }}>Loading...</div>
              ) : (
                <div className="statement-body">
                  <div className="statement-meta">
                    <div>
                      <strong>{statement.supplier.name}</strong>
                      {statement.supplier.phone ? <div>{statement.supplier.phone}</div> : null}
                      {statement.supplier.email ? <div>{statement.supplier.email}</div> : null}
                      {statement.supplier.address ? <div>{statement.supplier.address}</div> : null}
                    </div>
                    <div className="statement-period">
                      Period: {filters.from || '…'} to {filters.to || '…'}
                    </div>
                  </div>

                  <div className="statement-summary">
                    <div>
                      <span>Debit</span>
                      <strong>{formatCurrency(statement.summary.total_debit)}</strong>
                    </div>
                    <div>
                      <span>Credit</span>
                      <strong>{formatCurrency(statement.summary.total_credit)}</strong>
                    </div>
                    <div>
                      <span>Purchases</span>
                      <strong>{statement.summary.purchase_count}</strong>
                    </div>
                    <div>
                      <span>Balance</span>
                      <strong className={balanceClass(
                        statement.summary.balance > 0.001
                          ? 'due'
                          : statement.summary.balance < -0.001
                            ? 'credit'
                            : 'settled'
                      )}>
                        {formatCurrency(statement.summary.balance)}
                      </strong>
                    </div>
                  </div>

                  <table className="table statement-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Reference</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.transactions.length === 0 ? (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center' }}>
                            No transactions in this period
                          </td>
                        </tr>
                      ) : (
                        statement.transactions.map((tx) => (
                          <tr key={tx.id} className={`tx-row tx-${tx.type}`}>
                            <td>{formatDate(tx.date)}</td>
                            <td>
                              <span className={`tx-type tx-type-${tx.type}`}>
                                {tx.type}
                              </span>
                            </td>
                            <td>{tx.ref || '-'}</td>
                            <td>{tx.description}</td>
                            <td>{tx.debit ? formatCurrency(tx.debit) : '-'}</td>
                            <td>{tx.credit ? formatCurrency(tx.credit) : '-'}</td>
                            <td>{formatCurrency(tx.balance)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>

                  <div className="statement-actions no-print">
                    <button type="button" className="btn btn-primary" onClick={handlePrint}>
                      Print Statement
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setSelectedSupplier(null);
                        setStatement(null);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierReport;
